import { FiltrosFechaType } from '../components/FiltrosFecha';

// Función para cargar la configuración
const loadConfig = async () => {
  try {
    const response = await fetch('./config.json');
    const config = await response.json();
    return config.apiUrl;
  } catch (error) {
    console.warn('No se pudo cargar config.json, usando configuración por defecto');
    return 'http://localhost:5237/api';
  }
};

// Variable global para la URL del API
let API_BASE_URL = 'http://localhost:5237/api';
let configLoaded = false;

// Función para obtener la URL del API (espera a que se cargue la configuración)
const getApiUrl = async (): Promise<string> => {
  if (!configLoaded) {
    API_BASE_URL = await loadConfig();
    configLoaded = true;
    console.log('Configuración cargada getApiUrl:', API_BASE_URL);
  }
  return API_BASE_URL;
};

// Cargar configuración al inicializar
loadConfig().then(url => {
  API_BASE_URL = url;
  configLoaded = true;
  console.log('Configuración cargada loadConfig:', API_BASE_URL);
});

export interface HorasCliente {
  cliente: string;
  horas: number;
}

export interface HorasContratadas {
  tituloProyecto: string;
  horasContratadas: number;
}

export interface HorasComparacion {
  cliente: string;
  horasConsumidas: number;
  horasContratadas: number;
  porcentajeUtilizacion: number;
  estado: string;
}

export interface ContratoDetalle {
  contrato: string;
  horasContratadas: number;
  horasAplicadas: number;
  porcentajeUtilizacion: number;
  estado: string;
  fechaInicio?: string;
  fechaFin?: string;
}

export interface ClienteResumen {
  cliente: string;
  totalHorasContratadas: number;
  totalHorasAplicadas: number;
  porcentajeUtilizacionGeneral: number;
  contratos: ContratoDetalle[];
}

export interface CategoriaResumen {
  categoria: string;
  totalHorasContratadas: number;
  totalHorasAplicadas: number;
  porcentajeUtilizacionGeneral: number;
  clientes: ClienteResumen[];
}

export interface Categoria {
  categoria: string;
}

export interface Motivo {
  motivo: string;
}

export interface Producto {
  producto: string;
}

export interface ProductoResumen {
  producto: string;
  totalHorasContratadas: number;
  totalHorasAplicadas: number;
  porcentajeUtilizacionGeneral: number;
  categorias: CategoriaResumen[];
}

export interface DetalleHoras {
  numeroTicket: number;
  titulo: string;
  horas: number;
  motivo: string;
  estadoTicket: string;
  categoriaTicket: string;
  dia: number;
  fecha: string;
}

export interface Contratante {
  contratante: string;
}

export interface ContratanteResumen {
  contratante: string;
  totalHorasContratadas: number;
  totalHorasAplicadas: number;
  porcentajeUtilizacionGeneral: number;
  contratos: ContratoDetalle[];
}

export interface Contrato {
  tituloProyecto: string;
}

export const getHorasPorCliente = async (
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<HorasCliente[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/por-cliente`;
    const params = [];
    
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching horas por cliente:', error);
    throw error;
  }
};

export const getHorasContratadas = async (
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<HorasContratadas[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/contratadas`;
    const params = [];
    
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching horas contratadas:', error);
    throw error;
  }
};

export const getComparacionHoras = async (
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<HorasComparacion[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/comparacion`;
    const params = [];
    
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching comparación de horas:', error);
    throw error;
  }
};

export const getJerarquiaPorCliente = async (
  filtro?: string, 
  contratante?: string,
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<ClienteResumen[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/jerarquia-cliente`;
    const params = [];
    
    if (filtro) params.push(`filtro=${encodeURIComponent(filtro)}`);
    if (contratante) params.push(`contratante=${encodeURIComponent(contratante)}`);
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener datos de jerarquía por cliente');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getJerarquiaPorCategoria = async (
  filtro?: string, 
  contratante?: string,
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<CategoriaResumen[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/jerarquia-categoria`;
    const params = [];
    
    if (filtro) params.push(`filtro=${encodeURIComponent(filtro)}`);
    if (contratante) params.push(`contratante=${encodeURIComponent(contratante)}`);
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener datos de jerarquía por categoría');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getCategorias = async (): Promise<Categoria[]> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}/horas/categorias`);
    if (!response.ok) {
      throw new Error('Error al obtener categorías');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getMotivos = async (): Promise<Motivo[]> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}/horas/motivos`);
    if (!response.ok) {
      throw new Error('Error al obtener motivos');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getProductos = async (): Promise<Producto[]> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}/horas/productos`);
    if (!response.ok) {
      throw new Error('Error al obtener productos');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getJerarquiaPorProducto = async (
  filtro?: string, 
  contratante?: string,
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<ProductoResumen[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/jerarquia-producto`;
    const params = [];
    
    if (filtro) params.push(`filtro=${encodeURIComponent(filtro)}`);
    if (contratante) params.push(`contratante=${encodeURIComponent(contratante)}`);
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener datos de jerarquía por producto');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export interface DetalleContratoResponse {
  detalleHoras: DetalleHoras[];
  horasContratadas: number;
  periodoFiltrado: string;
  fechaInicio?: string;
  fechaFin?: string;
}

export const getDetalleHorasContrato = async (
  contrato: string, 
  fechaInicio?: Date,
  fechaFin?: Date,
  cliente?: string
): Promise<DetalleContratoResponse> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/detalle-contrato?contrato=${encodeURIComponent(contrato)}`;
    
    if (fechaInicio) {
      // Formatear fecha como YYYY-MM-DD sin hora
      const fechaInicioStr = fechaInicio.toISOString().split('T')[0];
      url += `&fechaInicio=${fechaInicioStr}`;
    }
    if (fechaFin) {
      // Formatear fecha como YYYY-MM-DD sin hora
      const fechaFinStr = fechaFin.toISOString().split('T')[0];
      url += `&fechaFin=${fechaFinStr}`;
    }
    if (cliente) url += `&cliente=${encodeURIComponent(cliente)}`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener detalle de horas del contrato');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getContratantes = async (): Promise<Contratante[]> => {
  try {
    const apiUrl = await getApiUrl();
    console.log('API_BASE_URL', `${apiUrl}/horas/contratantes`);
    const response = await fetch(`${apiUrl}/horas/contratantes`);
    if (!response.ok) {
      throw new Error('Error al obtener contratantes');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getJerarquiaPorContratante = async (
  filtro?: string,
  fechaDesde?: Date,
  fechaHasta?: Date,
  periodo?: string
): Promise<ContratanteResumen[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/jerarquia-contratante`;
    const params = [];
    
    if (filtro) params.push(`filtro=${encodeURIComponent(filtro)}`);
    if (fechaDesde) params.push(`fechaDesde=${fechaDesde.toISOString()}`);
    if (fechaHasta) params.push(`fechaHasta=${fechaHasta.toISOString()}`);
    if (periodo) params.push(`periodo=${encodeURIComponent(periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener datos de jerarquía por contratante');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getContratos = async (): Promise<Contrato[]> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}/horas/contratos`);
    if (!response.ok) {
      throw new Error('Error al obtener contratos');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};

export const getJerarquiaPorContrato = async (
  filtro?: string,
  filtrosFecha?: FiltrosFechaType
): Promise<ContratoDetalle[]> => {
  try {
    const apiUrl = await getApiUrl();
    let url = `${apiUrl}/horas/jerarquia-contrato`;
    const params = [];
    
    if (filtro) params.push(`filtro=${encodeURIComponent(filtro)}`);
    if (filtrosFecha?.fechaDesde) params.push(`fechaDesde=${filtrosFecha.fechaDesde.toISOString()}`);
    if (filtrosFecha?.fechaHasta) params.push(`fechaHasta=${filtrosFecha.fechaHasta.toISOString()}`);
    if (filtrosFecha?.periodo) params.push(`periodo=${encodeURIComponent(filtrosFecha.periodo)}`);
    
    if (params.length > 0) url += `?${params.join('&')}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Error al obtener jerarquía por contrato');
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}; 